/**
 * Tutorial library/index page (landing)
 *
 * Match the `/docs` landing page UX:
 * - section headings + card grid
 * - client-side filtering via `?q=...`
 */

import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Link, useLoaderData, useSearchParams } from "@remix-run/react";
import { useTranslation } from "react-i18next";

import { DocTutorialShell } from "#app/components/docs/DocTutorialShell";
import { TutorialSidebarNav } from "#app/components/tutorials/TutorialSidebarNav";
import { BrandCard } from "#app/components/ui/brand-card";
import {
  CardContent,
  CardHeader,
  CardTitle,
} from "#app/components/ui/card-legacy";
import { getRequestLocale } from "#app/i18n/get-request-locale.server";
import {
  loadTutorialIndex,
  type TutorialIndexItem,
} from "#app/lib/tutorials/loader";

export const meta: MetaFunction = () => [
  { title: "Tutorials | Supervity" },
  {
    name: "description",
    content: "Browse Supervity tutorials and step-by-step learning paths.",
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = await getRequestLocale(request);
  const tutorials = await loadTutorialIndex(locale);
  return json({ tutorials, locale });
}

function getTutorialSectionTitle(t: TutorialIndexItem): string {
  const primary = (t.slug.split("/")[0] || "other").toLowerCase();
  const secondary = (t.slug.split("/")[1] || "").toLowerCase();

  if (primary === "getting-started") return "Start Here";

  if (primary === "library") {
    if (secondary === "basic") return "Beginner";
    if (secondary === "intermediate") return "Intermediate";
    if (secondary === "advanced") return "Advanced";
    if (secondary === "integrations") return "Integrations";
    return "Library";
  }

  if (primary === "how-tos") return "How-tos";
  if (primary === "examples") return "Examples";

  return "Other";
}

function getFilename(t: TutorialIndexItem) {
  return t.path.split("/").pop() || t.slug.split("/").pop() || "";
}

function getFilenameNoExt(t: TutorialIndexItem) {
  return getFilename(t).replace(/\.md$/i, "");
}

function getOrderKey(t: TutorialIndexItem) {
  const base = getFilenameNoExt(t);
  const m = base.match(/^(\d+)[-_]/);
  const n = m ? Number(m[1]) : Number.POSITIVE_INFINITY;
  return { n, base, title: (t.metadata.title || "").toLowerCase() };
}

export default function TutorialsIndex() {
  const { t } = useTranslation("docs");
  const { tutorials } = useLoaderData<typeof loader>() as {
    tutorials: TutorialIndexItem[];
  };
  const [searchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const query = q.trim().toLowerCase();

  const visibleTutorials = query
    ? tutorials.filter((t) => {
        const haystack = [
          t.metadata.title,
          t.metadata.description,
          t.metadata.category,
          t.metadata.level,
          ...(t.metadata.tags || []),
          t.slug,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      })
    : tutorials;

  const grouped = visibleTutorials.reduce<Record<string, TutorialIndexItem[]>>(
    (acc, t) => {
      const key = getTutorialSectionTitle(t);
      acc[key] ||= [];
      acc[key]!.push(t);
      return acc;
    },
    {},
  );

  const sectionOrder = [
    "Start Here",
    "Beginner",
    "Intermediate",
    "Advanced",
    "Integrations",
    "Library",
    "How-tos",
    "Examples",
    "Other",
  ];

  const sectionsInOrder = sectionOrder.filter((s) => grouped[s]?.length);
  const extraSections = Object.keys(grouped)
    .filter((s) => !sectionOrder.includes(s))
    .sort((a, b) => a.localeCompare(b));
  const sections = [...sectionsInOrder, ...extraSections];
  const sectionLabels: Record<string, string> = {
    "Start Here": t("categories.startHere"),
    Beginner: t("categories.beginner"),
    Intermediate: t("categories.intermediate"),
    Advanced: t("categories.advanced"),
    Integrations: t("categories.integrations"),
    Library: t("categories.library"),
    "How-tos": t("categories.howTos"),
    Examples: t("categories.examples"),
    Other: t("categories.other"),
  };

  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });

  for (const section of sections) {
    grouped[section]!.sort((a, b) => {
      const ka = getOrderKey(a);
      const kb = getOrderKey(b);
      if (ka.n !== kb.n) return ka.n - kb.n;
      const byBase = collator.compare(ka.base, kb.base);
      if (byBase !== 0) return byBase;
      return collator.compare(ka.title, kb.title);
    });
  }

  return (
    <DocTutorialShell
      activeTab="tutorials"
      leftNav={<TutorialSidebarNav tutorials={tutorials} />}
    >
      <div className="space-y-8">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-[#141A42] dark:text-zinc-100">
            {t("tutorials")}
          </h1>
          <p className="text-muted-foreground">{t("tutorialsSubtitle")}</p>
        </div>

        {query ? (
          <div className="text-sm text-muted-foreground">
            {t("showingResultsFor", { query: q.trim() })}
          </div>
        ) : null}

        {query && visibleTutorials.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-300">
            {t("noTutorialsMatched")}
          </div>
        ) : null}

        {sections.map((section) => (
          <section key={section} className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#141A42] dark:text-zinc-100">
              {sectionLabels[section] ?? section}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {grouped[section]!.map((t) => (
                <Link
                  key={t.slug}
                  to={`/u/alpha/tutorials/${t.slug}`}
                  className="block"
                >
                  <BrandCard className="hover:border-[#D0D6ED]">
                    <CardHeader>
                      <CardTitle className="text-lg">
                        {t.metadata.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {t.metadata.description}
                      </p>
                    </CardContent>
                  </BrandCard>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </DocTutorialShell>
  );
}

export function shouldRevalidate() {
  // Index data doesn't depend on `q` query param. Filtering happens client-side.
  return false;
}
